// ENHANCED Dashboard.jsx with live stats and leaderboards
// FIXED VERSION - Improved error handling and data flow
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUser, SignOutButton } from '@clerk/clerk-react';
import './Dashboard.css';
import CreateGameModal from './CreateGameModal';
import FindGameModal from './FindGameModal';
import PageBG from './components/PageBG';
import { 
  testSupabaseConnection, 
  createUserProfile, 
  getUserProfile,
  getLeaderboard,
  getUserGameHistory
} from './lib/supabase';

export default function Dashboard() {
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isFindModalOpen, setIsFindModalOpen] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);
  
  // New state for live data
  const [userProfile, setUserProfile] = useState(null);
  const [userStats, setUserStats] = useState({
    total_games: 0,
    games_won: 0,
    highest_score: 0,
    rank_points: 0,
    win_rate: 0
  });
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auto-open create modal if coming from "Play Online Game" button
  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      setIsCreateModalOpen(true);
      setSearchParams({}); // Clear the query param
    }
  }, [searchParams, setSearchParams]);

  // Load user data and stats
  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Loading user data for:', user.firstName, 'Clerk ID:', user.id);
      
      // Test connection first
      const connected = await testSupabaseConnection();
      console.log('Supabase connected:', connected);
      
      if (!connected) {
        throw new Error('Failed to connect to Supabase');
      }

      // Create/get user profile - this now returns full profile data
      let profile = await createUserProfile(user);
      
      // If createUserProfile failed, try to get existing profile
      if (!profile) {
        console.log('createUserProfile failed, trying getUserProfile...');
        profile = await getUserProfile(user.id);
      }
      
      // If we still don't have a profile, there's a serious issue
      if (!profile) {
        throw new Error('Failed to create or retrieve user profile');
      }

      console.log('User profile loaded successfully:', profile);
      setUserProfile(profile);
      
      // Update user stats with actual data from profile
      setUserStats({
        total_games: profile.total_games || 0,
        games_won: profile.games_won || 0,
        games_lost: profile.games_lost || 0,
        highest_score: profile.highest_score || 0,
        rank_points: profile.rank_points || 0,
        win_rate: profile.total_games > 0 
          ? Math.round((profile.games_won / profile.total_games) * 100)
          : 0
      });
      
      // Load leaderboard
      try {
        const leaderboard = await getLeaderboard(10);
        setLeaderboardData(leaderboard);
        console.log('Leaderboard loaded:', leaderboard.length, 'players');
      } catch (leaderboardError) {
        console.error('Failed to load leaderboard:', leaderboardError);
        // Don't fail the entire load for leaderboard issues
      }
      
      // Load recent matches
      try {
        const matches = await getUserGameHistory(user.id, 5);
        setRecentMatches(matches);
        console.log('Recent matches loaded:', matches.length, 'matches');
      } catch (matchesError) {
        console.error('Failed to load recent matches:', matchesError);
        // Don't fail the entire load for matches issues
      }
      
    } catch (error) {
      console.error('Error loading user data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate user's leaderboard position
  const userRank = leaderboardData.findIndex(player => 
    player.username === userProfile?.username
  ) + 1;

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getGameTypeIcon = (gameType) => {
    return gameType === 'ranked' ? '🏆' : '🎮';
  };

  // Show error state if there's an error
  if (error) {
    return (
      <PageBG src="/bg/in_action.jpg">
        <div className="dashboard-wrapper">
          <div className="container">
            <div className="header">
              <div className="logo">
                <div className="logo-icon">F</div>
                FROZZLERS
              </div>
              <div className="nav-links">
                <button 
                  onClick={() => {
                    setIsGuestMode(true);
                    setIsCreateModalOpen(true);
                  }} 
                  className="btn btn-primary"
                >
                  Play Local Hot Seat
                </button>
                <Link to="/" className="btn btn-secondary">Home</Link>
                <SignOutButton>
                  <button className="btn btn-secondary">Sign Out</button>
                </SignOutButton>
              </div>
            </div>

            <div className="content-section">
              <div className="section-title" style={{ color: '#ef4444' }}>
                Error Loading Dashboard
              </div>
              <p style={{ color: '#e5e7eb', fontSize: '1.1rem', marginBottom: '1rem' }}>
                {error}
              </p>
              <button 
                onClick={loadUserData}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Retrying...' : 'Retry'}
              </button>
            </div>
          </div>
        </div>
      </PageBG>
    );
  }

  return (
    <PageBG src="/bg/in_action.jpg">
      <div className="dashboard-wrapper">
        {/* Fix the modal rendering with guest mode props */}
        {isCreateModalOpen && (
          <CreateGameModal 
            onClose={() => {
              setIsCreateModalOpen(false);
              setIsGuestMode(false);
            }} 
            isGuestMode={isGuestMode}
          />
        )}
        {isFindModalOpen && <FindGameModal onClose={() => setIsFindModalOpen(false)} />}

        <div className="container">
          <div className="header">
            <div className="logo">
              <div className="logo-icon">F</div>
              FROZZLERS
            </div>
            <div className="nav-links">
              {/* Change the Play Local Hot Seat link to a button */}
              <button 
                onClick={() => {
                  setIsGuestMode(true);
                  setIsCreateModalOpen(true);
                }} 
                className="btn btn-primary"
              >
                Play Local Hot Seat
              </button>
              <Link to="/" className="btn btn-secondary">Home</Link>
              <SignOutButton>
                <button className="btn btn-secondary">Sign Out</button>
              </SignOutButton>
            </div>
          </div>

          <div className="content-section">
            <div className="section-title">
              Welcome Back, {user ? user.firstName : 'Player'}!
              {loading && <span style={{ fontSize: '14px', color: '#9ca3af', marginLeft: '10px' }}>Loading stats...</span>}
              {userProfile && !loading && (
                <span style={{ fontSize: '14px', color: '#10b981', marginLeft: '10px' }}>✓ Connected</span>
              )}
            </div>
            <p style={{ color: '#e5e7eb', fontSize: '1.1rem' }}>
              Ready to dominate the ice? Create a new game or find an open match.
            </p>
          </div>

          {/* Play Now Section */}
          <div className="content-section">
            <div className="section-title">Play Now</div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button className="btn btn-primary" onClick={() => setIsCreateModalOpen(true)} style={{ flex: 1, padding: '1.5rem' }}>
                Create Game
              </button>
              <button className="btn btn-secondary" onClick={() => setIsFindModalOpen(true)} style={{ flex: 1, padding: '1.5rem' }}>
                Find Game
              </button>
            </div>
          </div>

          {/* Live Stats Grid */}
          <div className="dashboard-grid">
            <div className="stat-card">
              <div className="stat-number">{loading ? '...' : userStats.total_games}</div>
              <div className="stat-label">Games Played</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{loading ? '...' : userStats.games_won}</div>
              <div className="stat-label">Wins</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{loading ? '...' : userStats.highest_score}</div>
              <div className="stat-label">Best Score</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {loading ? '...' : userRank > 0 ? `#${userRank}` : '-'}
              </div>
              <div className="stat-label">Ranking</div>
            </div>
          </div>

          {/* Additional Stats Row */}
          <div className="dashboard-grid" style={{ marginTop: '1rem' }}>
            <div className="stat-card">
              <div className="stat-number">{loading ? '...' : `${userStats.win_rate}%`}</div>
              <div className="stat-label">Win Rate</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{loading ? '...' : userStats.rank_points}</div>
              <div className="stat-label">Rank Points</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{loading ? '...' : userStats.games_lost || 0}</div>
              <div className="stat-label">Losses</div>
            </div>
            <div className="stat-card">
              <button 
                onClick={loadUserData}
                className="btn btn-secondary"
                style={{ 
                  padding: '8px 16px', 
                  fontSize: '12px',
                  height: 'auto',
                  background: '#374151',
                  border: '1px solid #4b5563'
                }}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : '🔄 Refresh'}
              </button>
              <div className="stat-label">Update Stats</div>
            </div>
          </div>

          {/* Recent Matches */}
          <div className="content-section">
            <div className="section-title">Recent Matches</div>
            {loading ? (
              <div className="coming-soon">Loading recent matches...</div>
            ) : recentMatches.length === 0 ? (
              <div className="coming-soon">
                No matches yet. <Link to="/game" style={{ color: '#60a5fa' }}>Play your first game!</Link>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {recentMatches.map((match, index) => (
                  <div key={index} className="leaderboard-item" style={{ justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span>{getGameTypeIcon(match.games?.game_type || 'casual')}</span>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#e5e7eb' }}>
                          {match.final_score} points
                        </div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                          {match.games?.player_count || 2}P • {match.games?.pucks_per_turn || 5} pucks
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '12px', color: '#9ca3af' }}>
                      <div>#{match.player_position || 'N/A'}</div>
                      <div>{formatDate(match.games?.finished_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Live Leaderboard */}
          <div className="content-section">
            <div className="section-title">
              Global Leaderboard
              {!loading && leaderboardData.length > 0 && (
                <span style={{ fontSize: '14px', color: '#9ca3af', marginLeft: '10px' }}>
                  Live data from {leaderboardData.length} players
                </span>
              )}
            </div>
            {loading ? (
              <div className="coming-soon">Loading leaderboard...</div>
            ) : leaderboardData.length === 0 ? (
              <div className="coming-soon">No players on leaderboard yet. Be the first!</div>
            ) : (
              leaderboardData.map((player, index) => {
                const isCurrentUser = player.username === userProfile?.username;
                return (
                  <div 
                    key={index} 
                    className="leaderboard-item"
                    style={isCurrentUser ? { 
                      background: 'rgba(96, 165, 250, 0.1)', 
                      border: '1px solid rgba(96, 165, 250, 0.3)' 
                    } : {}}
                  >
                    <div className="rank">#{index + 1}</div>
                    <div className="player-name" style={isCurrentUser ? { color: '#60a5fa', fontWeight: 'bold' } : {}}>
                      {player.username}
                      {isCurrentUser && <span style={{ marginLeft: '8px', fontSize: '12px' }}>(You)</span>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', fontSize: '12px' }}>
                      <div className="score">{player.rank_points || 0} pts</div>
                      <div style={{ color: '#9ca3af' }}>
                        {player.games_won}W-{player.games_lost}L ({player.win_rate}%)
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </PageBG>
  );
}
